import { useState } from "react";
import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateFields } from "../../../redux/profileSlice";
import RoundedTextField from "../../../components/RoundedProfileTextField";
import { profileOptionButtonStyles } from "./styles";
import { useEditableProfile } from "../../../hooks/useEditableProfile";
import {
  GENDER_OPTIONS,
  isKnownGenderLabel,
  normalizeGenderIdentities,
  SELF_DESCRIBE,
  toggleDemographicValue,
} from "../../../constants/demographicOptions";

const EditGenderIdentity = () => {
  // Router state when opened from the Profile UI, the stored profile on a
  // reload or a pasted link (ECH-387). `RequireProfileLoaded` guarantees one
  // of them exists before this component mounts, so the initialisers below
  // are safe to dereference.
  const { profile: localProfile } = useEditableProfile();
  const demographics = localProfile?.demographics ?? {};

  const [selected, setSelected] = useState(() =>
    normalizeGenderIdentities(demographics.genderIdentities),
  );

  const [customGender, setCustomGender] = useState(
    normalizeGenderIdentities(demographics.genderIdentities).includes(
      SELF_DESCRIBE,
    )
      ? (demographics.customGender ?? "")
      : "",
  );

  const [isChecked, setIsChecked] = useState(demographics.genderVisible);
  const hasAnswer = selected.length > 0;

  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const handleGenderChange = (value) => {
    setErrorMsg("");
    const next = toggleDemographicValue(selected, value);

    setSelected(next);
    if (!next.includes(SELF_DESCRIBE)) {
      setCustomGender("");
    }
  };

  const handleCheckboxChange = (event) => {
    if (!hasAnswer) return;
    setIsChecked(event.target.checked);
    dispatch(
      updateFields({
        field: "demographics.genderVisible",
        value: event.target.checked,
      }),
    );
  };

  if (!localProfile) {
    return <div>No profile data found.</div>;
  }
  return (
    <Background>
      <Topnav />
      <Box className="selection-box">
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "26px",
              xl: "32px",
            },
          }}
        >
          Gender Identity
        </Typography>
        <Box sx={{ marginTop: "20px" }}>
          <Box className="w-full flex gap-2 flex-wrap">
            {GENDER_OPTIONS.map((gender) => {
              return (
                <Box sx={{ width: "48%" }} key={gender?.value}>
                  <Button
                    key={gender?.value}
                    sx={{
                      height: {
                        xs: "40px",
                        sm: "45px",
                        md: "45px",
                        lg: "50px",
                        xl: "66px",
                      },
                      borderRadius: "50px",
                      width: "100%",
                      border: "1px solid #AAB6C8",
                      backgroundColor: "#ffffff",
                      color: "#2A4876",
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "16px",
                        lg: "16px",
                        xl: "20px",
                      },
                      fontWeight: "400",
                      textAlign: "center",
                      textTransform: "capitalize",
                    }}
                    style={{
                      ...profileOptionButtonStyles,
                      backgroundColor: selected.includes(gender.value)
                        ? "#7BB9D5"
                        : "white",
                      borderColor: selected.includes(gender.value)
                        ? "#7BB9D5"
                        : "#AAB6C8",
                    }}
                    aria-pressed={selected.includes(gender.value)}
                    onClick={() => handleGenderChange(gender.value)}
                  >
                    {gender.label}
                  </Button>
                </Box>
              );
            })}
          </Box>
        </Box>
        {selected.includes(SELF_DESCRIBE) && (
          <Box
            sx={{
              marginTop: {
                xs: "10px",
                sm: "10px",
                md: "10px",
                lg: "10px",
                xl: "20px",
              },
            }}
          >
            <Typography
              sx={{
                textAlign: "left",
                fontSize: "14px",
                fontWeight: 400,
                marginBottom: "5px",
                color: "#152134",
              }}
            >
              {/* Please Specify (If Comfortable) */}
              Please Specify
            </Typography>
            <RoundedTextField
              name="customGender"
              variant="outlined"
              placeholder="Enter here"
              fullWidth
              value={customGender}
              onChange={(e) => {
                setCustomGender(e.target.value);
              }}
              slotProps={{
                input: {
                  "aria-label": "custom gender input",
                },
              }}
            />
          </Box>
        )}
        {errorMsg && <Typography sx={{ color: "red" }}>{errorMsg}</Typography>}
        <FormControlLabel
          sx={{
            display: "flex",
            justifyContent: "start",
            textAlign: "left",
            marginLeft: 0,
            marginTop: "12px",
          }}
          control={
            <Checkbox
              color="primary"
              checked={hasAnswer && Boolean(isChecked)}
              disabled={!hasAnswer}
              onChange={handleCheckboxChange}
              sx={{
                margin: 0,
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&.Mui-checked": {
                  color: "#7BB9D5",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ textAlign: "left", paddingLeft: "8px" }}
              variant="body5"
              className="text-xs text-text-color"
            >
              Display my gender on my profile
            </Typography>
          }
        />
        <Button
          variant="contained"
          fullWidth
          className="Send-button"
          sx={{
            backgroundColor: "#2A4876",
            color: "#fff",
            marginTop: "20px !important",
          }}
          disabled={
            selected.length === 0 ||
            (selected.includes(SELF_DESCRIBE) && !customGender.trim())
          }
          onClick={() => {
            const trimmedCustomGender = customGender.trim();
            if (selected.includes(SELF_DESCRIBE) && !trimmedCustomGender) {
              setErrorMsg("Please describe your gender identity.");
              return;
            }
            if (
              selected.includes(SELF_DESCRIBE) &&
              isKnownGenderLabel(trimmedCustomGender)
            ) {
              setErrorMsg(
                "Please choose the listed identity instead of self-describing it.",
              );
              return;
            }
            const originalIdentities = demographics.genderIdentities ?? [];
            const identitiesChanged =
              originalIdentities.length !== selected.length ||
              originalIdentities.some(
                (identity, index) => identity !== selected[index],
              );
            if (identitiesChanged) {
              dispatch(
                updateFields({
                  field: "demographics.genderIdentities",
                  value: selected,
                }),
              );
            }

            const nextCustomGender = selected.includes(SELF_DESCRIBE)
              ? trimmedCustomGender
              : "";
            if ((demographics.customGender ?? "") !== nextCustomGender) {
              dispatch(
                updateFields({
                  field: "demographics.customGender",
                  value: nextCustomGender,
                }),
              );
            }
            navigate("/profile/edit-profile");
          }}
        >
          Save
        </Button>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditGenderIdentity;
